import { useRef } from "react"

// libraries
import { useQuery, useQueryClient } from "@tanstack/react-query"

// styles
import "@/assets/scss/app/upcoming-todos.scss"

// helpers
import Api from "@/_helpers/Api"

// components
import SingleUpcomingTodo from "./SingleUpcomingTodo"
import View from "@/components/pages/todo/partials/modals/View"
import AddOrEdit from "@/components/pages/todo/partials/modals/AddOrEdit"
import DeleteQuestion from "@/components/modals/messages/Question"
import RepeatQuestion from "@/components/modals/messages/Question"

// providers
import { trans } from "@/_providers/Translation"
import { enqueueSnackbar } from "notistack"

export default function UpcomingTodos(props) {
  const deleteModalRef = useRef(null)
  const modalAddOrEditRef = useRef(null)
  const viewModalRef = useRef(null)
  const repeatModalRef = useRef(null)

  const queryClient = useQueryClient()

  const {
    data: todos,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["todos/new"],
    queryFn: async () => {
      const response = await Api.get("todos/new")

      return response.data
    },
    initialData: [],
  })

  // delete
  const handleDelete = (id) => {
    deleteModalRef.current.open(id)

    deleteModalRef.current.onSuccess(() => {
      handleDestroy(id)
    })
  }

  const handleDestroy = (id) => {
    Api.post("todos/delete", {
      id,
    }).then((res) => {
      refreshTodos()
      enqueueSnackbar(`Бележката беше изтрита!`, {
        variant: "success",
      })
    })
  }

  const handleRead = (id) => {
    Api.post("todos/mark-as-read", {
      id,
    }).then((res) => {
      refreshTodos()
      enqueueSnackbar(`Бележката беше маркирана като прочетена.`, {
        variant: "info",
      })
    })
  }

  const handleComplete = (id) => {
    Api.post("todos/mark-as-completed", {
      id,
    }).then((res) => {
      refreshTodos()
      enqueueSnackbar(`Бележката беше маркирана като завършена.`, {
        variant: "success",
      })
    })
  }

  const handleTogglePause = (id) => {
    Api.post("todos/toggle-pause", {
      id,
    }).then((res) => {
      refreshTodos()
      enqueueSnackbar(
        `Бележката беше ${res.data?.paused_at ? "поставена на пауза" : "възобновена"}.`,
        {
          variant: "success",
        },
      )
    })
  }

  const handleToggleFlag = (id) => {
    Api.post(`todos/${id}/toggle-flag`).then((res) => {
      refreshTodos()
      enqueueSnackbar(
        `${trans('messages.todoMarkedAs')} ${res.data?.flagged ? trans('messages.flagged') : trans('messages.unflagged')}.`,
        {
          variant: "success",
        },
      )
    })
  }

  const handleEdit = (id) => {
    modalAddOrEditRef.current.edit(id)
  }

  const handleView = (data) => {
    viewModalRef.current.open(data)
  }

  const handleStopRepeat = (id) => {
    let modal = repeatModalRef.current

    modal.open()
    modal.onSuccess(() => {
      Api.post("todos/stop-repeat", {
        id,
      }).then((res) => {
        refreshTodos()
      })
    })
  }

  function refreshTodos() {
    queryClient.invalidateQueries({ queryKey: ["todos/new"] })
    queryClient.invalidateQueries({ queryKey: ["todos/all"] })
  }

  return (
    <>
      <View ref={viewModalRef} handleRead={handleRead} />
      <DeleteQuestion
        ref={deleteModalRef}
        mainMessage={trans("headings.confirmDelete")}
        secondaryMessage={trans("subheadings.confirmDelete")}
      />
      <RepeatQuestion
        ref={repeatModalRef}
        mainMessage={trans("headings.question1")}
      />
      <AddOrEdit
        ref={modalAddOrEditRef}
        handleCreate={() => {
          refreshTodos()
        }}
        handleUpdate={() => {
          refreshTodos()
        }}
      />

      <ul
        className="upcoming-todos-list"
        style={
          isFetching
            ? {
                opacity: 0.4,
                pointerEvents: "none",
              }
            : {}
        }
      >
        {todos.map((todo) => (
          <SingleUpcomingTodo
            key={todo.id}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onComplete={handleComplete}
            onTogglePause={handleTogglePause}
            onView={handleView}
            onStopRepeat={handleStopRepeat}
            onToggleFlag={handleToggleFlag}
            todo={todo}
          />
        ))}
      </ul>
    </>
  )
}
